/**
 * Local state for per-node accept/reject decisions within a proposal review.
 *
 * The user stages decisions client-side — one `accept` or `reject` per changed
 * node — and then submits the whole set to the server in a single apply. All
 * changes default to `accept` because the common case is "this looks right,
 * ship it"; the user can reject individual rows or hit Reject all.
 *
 * Pure state; no fetching. Mutations for the apply/reject call live in
 * useWorkflowProposals.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SnapshotDiff } from './diffSnapshot';

export type Decision = 'accept' | 'reject';

export interface ReviewDecisionsApi {
  decisions: Record<string, Decision>;
  setDecision: (nodeId: string, decision: Decision) => void;
  toggle: (nodeId: string) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  counts: { accepted: number; rejected: number; total: number };
  /** Node ids with `accept` — what we submit to the server. */
  acceptedNodeIds: string[];
  /** True when every change row is set to accept. */
  allAccepted: boolean;
  /** True when every change row is set to reject. */
  allRejected: boolean;
}

function buildInitial(diff: SnapshotDiff): Record<string, Decision> {
  const out: Record<string, Decision> = {};
  for (const n of diff.addedNodes) out[n.id] = 'accept';
  for (const n of diff.removedNodes) out[n.id] = 'accept';
  for (const n of diff.changedNodes) out[n.id] = 'accept';
  return out;
}

/**
 * Signature of the set of change node ids in the diff. Used to detect when
 * the diff has meaningfully shifted (e.g. proposal finished loading after an
 * initial render with `null`) so we can (re)initialize the decision map.
 */
function diffSignature(diff: SnapshotDiff | null): string {
  if (!diff) return '';
  const ids = [
    ...diff.addedNodes.map((n) => `a:${n.id}`),
    ...diff.removedNodes.map((n) => `r:${n.id}`),
    ...diff.changedNodes.map((n) => `c:${n.id}`),
  ];
  ids.sort();
  return ids.join('|');
}

/**
 * @param diff  Output of `diffSnapshots()`.
 * @param resetKey  An identifier that, when changed, resets the decision map.
 *                  Typically the proposal id — switching proposals wipes stale
 *                  decisions.
 */
export function useReviewDecisions(
  diff: SnapshotDiff | null,
  resetKey: string | null,
): ReviewDecisionsApi {
  const [decisions, setDecisions] = useState<Record<string, Decision>>(() =>
    diff ? buildInitial(diff) : {},
  );

  // Reset fully on proposal change. On *diff* change (a background refetch
  // bringing new / dropped change rows) we MERGE instead: preserve any
  // existing decisions, add defaults for newly-appearing ids, drop entries
  // for ids that are no longer in the diff. This keeps a user's in-progress
  // cherry-picks intact when the query refetches on focus.
  const initState = useRef<{ resetKey: string | null; signature: string }>({
    resetKey,
    signature: diffSignature(diff),
  });
  useEffect(() => {
    if (!diff) return;
    const signature = diffSignature(diff);
    const prev = initState.current;
    if (prev.resetKey === resetKey && prev.signature === signature) return;

    if (prev.resetKey !== resetKey) {
      // Different proposal entirely — wipe and start fresh.
      initState.current = { resetKey, signature };
      setDecisions(buildInitial(diff));
      return;
    }

    // Same proposal, diff shifted — merge.
    initState.current = { resetKey, signature };
    const liveIds = new Set<string>([
      ...diff.addedNodes.map((n) => n.id),
      ...diff.removedNodes.map((n) => n.id),
      ...diff.changedNodes.map((n) => n.id),
    ]);
    setDecisions((existing) => {
      // Bail if the merged map is identical to the existing one — no need
      // to allocate a new object and re-render every row.
      const existingKeys = Object.keys(existing);
      if (existingKeys.length === liveIds.size) {
        let same = true;
        for (const id of liveIds) {
          if (!(id in existing)) {
            same = false;
            break;
          }
        }
        if (same) return existing;
      }
      const next: Record<string, Decision> = {};
      for (const id of liveIds) next[id] = existing[id] ?? 'accept';
      return next;
    });
  }, [diff, resetKey]);

  const setDecision = useCallback((nodeId: string, decision: Decision) => {
    setDecisions((prev) => (prev[nodeId] === decision ? prev : { ...prev, [nodeId]: decision }));
  }, []);

  const toggle = useCallback((nodeId: string) => {
    setDecisions((prev) => ({
      ...prev,
      [nodeId]: prev[nodeId] === 'accept' ? 'reject' : 'accept',
    }));
  }, []);

  const setAll = useCallback((d: Decision) => {
    setDecisions((prev) => {
      const ids = Object.keys(prev);
      if (ids.every((id) => prev[id] === d)) return prev;
      const next: Record<string, Decision> = {};
      for (const id of ids) next[id] = d;
      return next;
    });
  }, []);
  const acceptAll = useCallback(() => setAll('accept'), [setAll]);
  const rejectAll = useCallback(() => setAll('reject'), [setAll]);

  return useMemo<ReviewDecisionsApi>(() => {
    const ids = Object.keys(decisions);
    let accepted = 0;
    const acceptedIds: string[] = [];
    for (const id of ids) {
      if (decisions[id] === 'accept') {
        accepted += 1;
        acceptedIds.push(id);
      }
    }
    const total = ids.length;
    const rejected = total - accepted;
    return {
      decisions,
      setDecision,
      toggle,
      acceptAll,
      rejectAll,
      counts: { accepted, rejected, total },
      acceptedNodeIds: acceptedIds,
      allAccepted: total > 0 && accepted === total,
      allRejected: total > 0 && rejected === total,
    };
  }, [decisions, setDecision, toggle, acceptAll, rejectAll]);
}
